import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPOInvoices,
  toggleDeleteVendorInvoice,
  downloadProjectPO
} from "../../actions/project-management.action";
import { getGlobalSettingTemplateTypes } from "../../actions/global-setting-template.action";
import { getGlobalSettingReducer } from "../../actions/global-setting.action";
import { format } from "date-fns";
import idx from "idx";
import _ from "lodash";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Button from 'react-bootstrap/Button';
import { roundValue, toDecimal } from 'src/util/decimal-utils';
import { toUSD } from "src/util/currency-formatter";

import CustomTable from 'src/components/table/table';
import DownloadDialog from 'src/components/download-dialog';
import Modal from 'src/components/modal/modal';
import InvoiceClaimsPanel from 'src/components/invoice-claims-panel';

function WorkOrderInvoicesSection(props) {
  const {
    poUuid,
    projectId,
    onNavigate,
    vendorType
  } = props;

  const dispatch = useDispatch();
  const letterhead = useSelector((state) => state.globalSetting.letterhead);

  const [invoices, setInvoices] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadInvoiceId, setDownloadInvoiceId] = useState("");
  const [templateOptions, setTemplateOptions] = useState([]);

  useEffect(() => {
    if (poUuid) {
      fetchInvoices(poUuid);
    }
  }, [poUuid]);

  useEffect(() => {
    if (_.isEmpty(letterhead)) {
      dispatch(getGlobalSettingReducer('LETTERHEAD'));
    }
  }, [letterhead, dispatch]);

  const fetchInvoices = async (poId) => {
    try {
      const response = await dispatch(getPOInvoices(poId));
      const invoiceList = idx(response, _ => _.invoice) || [];
      setInvoices(invoiceList);
    } catch (error) {
      console.error("Error fetching WO invoices:", error);
    }
  };

  const handleDownloadDialog = async (id = "") => {
    const nextOpen = !downloadOpen;
    setDownloadOpen(nextOpen);
    if (nextOpen && id) {
      setDownloadInvoiceId(id);
      try {
        const templateData = await dispatch(getGlobalSettingTemplateTypes("subcontractor_payment_schedule", projectId));
        const options = [];
        if (Array.isArray(templateData)) {
          templateData.forEach(item => {
            options.push({ value: item.id, key: item.name });
          });
        }
        setTemplateOptions(options);
      } catch (err) {
        console.error("Error loading template options:", err);
      }
    }
  }

  const downloadFile = async (fileFormat = "pdf", templateValue) => {
    try {
      await dispatch(downloadProjectPO(downloadInvoiceId, fileFormat, templateValue));
      setDownloadOpen(false);
    } catch (e) {
      console.error("Error downloading file:", e);
    }
  }

  const handleDeleteInvoicePrompt = (invoiceId) => {
    setDeleteInvoiceId(invoiceId);
    setDeleteDialogOpen(true);
  }

  const handleConfirmDelete = async () => {
    try {
      await dispatch(toggleDeleteVendorInvoice(deleteInvoiceId))
      setDeleteDialogOpen(false)
      if (props.onDeleted) {
        props.onDeleted(deleteInvoiceId);
      }
      fetchInvoices(poUuid);
    } catch (error) {
      console.error("Error deleting invoice:", error)
    }
  }

  if (vendorType !== "sub-contractor" || invoices.length === 0) {
    return null;
  }

  // Summary Metrics calculations using exact Decimal computations
  let sumClaimed = toDecimal(0);
  let sumRetention = toDecimal(0);
  let sumApproved = toDecimal(0);

  invoices.forEach(inv => {
    sumClaimed = sumClaimed.plus(toDecimal(inv.invoiceClaim));
    sumRetention = sumRetention.plus(toDecimal(inv.retentionAmount));
    sumApproved = sumApproved.plus(toDecimal(inv.approvedAmount));
  });

  const formattedClaimed = toUSD(roundValue(sumClaimed, 2));
  const formattedRetention = toUSD(roundValue(sumRetention, 2));
  const formattedApproved = toUSD(roundValue(sumApproved, 2));

  // Invoice Columns
  const invoiceColumns = [
    {
      title: 'Progress Claim Number',
      field: 'progress_claim_number',
      render: (rowData) => rowData.isSummary ? (
        <strong>Total Claims Approved</strong>
      ) : (
        <span>{rowData.progress_claim_number || '-'}</span>
      )
    },
    {
      title: 'Date',
      field: 'invoiceDate',
      render: (rowData) => rowData.isSummary ? '' : (rowData.invoiceDate ? format(new Date(rowData.invoiceDate), 'dd/MM/yyyy') : (rowData.createdAt ? format(new Date(rowData.createdAt), 'dd/MM/yyyy') : ''))
    },
    {
      title: 'Invoice Number',
      field: 'invoice_no',
      render: (rowData) => rowData.isSummary ? '' : (
        <span className="link cursor-pointer text-primary text-decoration-underline" onClick={() => onNavigate(rowData.id)}>
          {rowData.invoice_no || '-'}
        </span>
      )
    },
    {
      title: 'Amount Claimed',
      field: 'invoiceClaim',
      render: (rowData) => rowData.isSummary ? <strong>{toUSD(rowData.totalAmountClaimed)}</strong> : <span>{toUSD(rowData.invoiceClaim)}</span>
    },
    {
      title: 'Retention',
      field: 'retentionAmount',
      render: (rowData) => {
        if (rowData.isSummary) {
          return <span>{toUSD(rowData.totalRetention)}</span>;
        }
        const retentionAmount = roundValue(toDecimal(rowData.retentionAmount), 2);
        if (retentionAmount > 0) {
          return <strong className="text-danger">-{toUSD(retentionAmount)}</strong>;
        }
        return <strong className="text-success">{toUSD(retentionAmount)}</strong>;
      }
    },
    {
      title: 'Approved',
      field: 'approvedAmount',
      render: (rowData) => rowData.isSummary ? <strong>{toUSD(rowData.totalApproved)}</strong> : <span>{toUSD(rowData.approvedAmount)}</span>
    },
    {
      title: 'Sub Total',
      field: 'subTotal',
      render: (rowData) => {
        if (rowData.isSummary) {
          const subTotal = toDecimal(rowData.totalApproved).minus(rowData.totalRetention);
          return <strong>{toUSD(subTotal)}</strong>
        }

        const subTotal = toDecimal(rowData.approvedAmount).minus(rowData.retentionAmount);
        return <span>{toUSD(subTotal)}</span>
      }
    },
    {
      title: 'GST',
      field: 'taxAmount',
      render: (rowData) => {
        if (rowData.isSummary) {
          return <strong>{toUSD(rowData.totalGst)}</strong>;
        }
        const taxAmount = roundValue(toDecimal(rowData.taxAmount), 2);
        if (taxAmount > 0) {
          return <span>{toUSD(taxAmount)}</span>;
        }
        const approved = roundValue(toDecimal(rowData.approvedAmount), 2);
        const gstRate = toDecimal(letterhead?.finance_gst || 0).dividedBy(100);
        return <span>{toUSD(roundValue(toDecimal(approved).times(gstRate), 2))}</span>;
      }
    },
    {
      title: 'Total Inc GST',
      field: 'totalClaimedIncGst',
      render: (rowData) => {
        if (rowData.isSummary) {
          return <strong>{toUSD(rowData.totalClaimedIncGst)}</strong>;
        }
        const approved = toDecimal(rowData.approvedAmount);
        const taxAmount = roundValue(toDecimal(rowData.taxAmount), 2);
        const retention = toDecimal(rowData.retentionAmount);
        const gstRate = toDecimal(letterhead?.finance_gst || 0).dividedBy(100);
        const gstAmount = taxAmount > 0 ? toDecimal(taxAmount) : approved.times(gstRate);
        const totalIncGst = approved.minus(retention).plus(gstAmount);
        return <span>{toUSD(roundValue(totalIncGst, 2))}</span>;
      }
    },
    {
      title: 'Action',
      field: 'action',
      render: (rowData) => rowData.isSummary ? '' : (
        <div className="action-dropdown">
          <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={(
            <Popover id="popover-basic">
              <Popover.Body>
                <ul className="action-block-list mb-0">
                  <li onClick={() => onNavigate(rowData.id)}>View Invoice</li>
                  <li onClick={() => handleDownloadDialog(rowData.id)}>Download Payment Summary</li>
                  <li onClick={() => handleDeleteInvoicePrompt(rowData.id)} className="text-danger">Delete</li>
                </ul>
              </Popover.Body>
            </Popover>
          )}>
            <Button variant="default" data-cy="action-menu-button">
              <MoreHorizIcon />
            </Button>
          </OverlayTrigger>
        </div>
      )
    }
  ];

  // Pre-process invoice data with summary row for table
  const woTableData = (() => {
    const rows = invoices.map(item => ({ ...item, isSummary: false }));
    const totals = rows.reduce((acc, item) => {
      const claimAmount = toDecimal(item.invoiceClaim);
      const retention = toDecimal(item.retentionAmount);
      const approved = toDecimal(item.approvedAmount);
      const taxAmount = roundValue(toDecimal(item.taxAmount), 2);
      const gstRate = toDecimal(letterhead?.finance_gst || 0).dividedBy(100);
      const gst = taxAmount > 0 ? toDecimal(taxAmount) : approved.times(gstRate);
      acc.totalAmountClaimed = acc.totalAmountClaimed.plus(claimAmount);
      acc.totalRetention = acc.totalRetention.plus(retention);
      acc.totalApproved = acc.totalApproved.plus(approved);
      acc.totalGst = acc.totalGst.plus(gst);
      acc.totalClaimedIncGst = acc.totalClaimedIncGst.plus(approved.minus(retention).plus(gst));
      return acc;
    }, { totalAmountClaimed: toDecimal(0), totalRetention: toDecimal(0), totalApproved: toDecimal(0), totalGst: toDecimal(0), totalClaimedIncGst: toDecimal(0) });
    if (rows.length > 0) {
      rows.push({
        id: 'summary',
        isSummary: true,
        totalAmountClaimed: roundValue(totals.totalAmountClaimed, 2),
        totalRetention: roundValue(totals.totalRetention, 2),
        totalApproved: roundValue(totals.totalApproved, 2),
        totalGst: roundValue(totals.totalGst, 2),
        totalClaimedIncGst: roundValue(totals.totalClaimedIncGst, 2),
        className: 'summary-row'
      });
    }
    return rows;
  })();

  return (
    <div className="col-12 mt-5">
      <hr className="mt-2 mb-4" />
      <h3 className="section-title text-uppercase mb-4">Work Order Invoices</h3>

      {/* Summary Cards + Claims Table */}
      <InvoiceClaimsPanel
        claimed={formattedClaimed}
        retention={formattedRetention}
        approved={formattedApproved}
        columns={invoiceColumns}
        data={woTableData}
        exportButton={false}
        enableSearch={false}
        toolbar={false}
        dataCy="wo-invoices-table"
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        modalHeading="Confirm Delete"
        maxWidth="sm"
        showActionBtn
        savebtnClass="createBtn"
        cxlbtnClass="cancelBtn"
        modalSaveTxt="Delete"
        modalCloseTxt="Cancel"
        onClick={handleConfirmDelete}
      >
        <div className="p-4 text-center">
          <p className="mb-2">Are you sure you want to delete this invoice?</p>
          <p className="text-danger fw-bold mb-0">
            This action cannot be undone.
          </p>
        </div>
      </Modal>

      {/* Download Template Dialog */}
      <DownloadDialog
        open={downloadOpen}
        id={downloadInvoiceId}
        downloadFile={downloadFile}
        handleDownloadDialog={handleDownloadDialog}
        templateOptions={templateOptions}
        downloadModuleName="Payment Summary"
      />
    </div>
  );
}

export default WorkOrderInvoicesSection;
